// src/pages/Employment/Employment.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { 
  FaEye, 
  FaEdit, 
  FaTrash, 
  FaPlus, 
  FaSearch, 
  FaTimes,
  FaBuilding,
  FaMapMarkerAlt,
  FaClock,
  FaCheckCircle,
  FaSpinner,
  FaExclamationCircle,
  FaStar,
  FaUser,
  FaBriefcase,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaFilter,
} from 'react-icons/fa';
import {   FaSyncAlt } from "react-icons/fa";

import CommonBreadcrumb from '../../common/CommonBreadcrumb';
import { useDispatch, useSelector } from "react-redux";
import { successToast, errorToast } from '../../Utils/toast';
import { openModal } from "../../redux/features/modalSlice";
import Pagination from '../../common/Pagination';
import SearchBar from '../../common/Search';
import { setEmploymentFilters } from '../../redux/features/filtersSlice';
import { 
  useGetAdminJobsQuery,
  useGetJobStatisticsQuery,
  useUpdateJobStatusMutation,
  useDeleteJobMutation,
} from "../../redux/services/employmentService/employment.Services";

function Employment() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const filter = useSelector((state) => state.filters?.employment || {});
  
  const [statusFilter, setStatusFilter] = useState(filter.status || '');
  const [categoryFilter, setCategoryFilter] = useState(filter.category || '');
  const [typeFilter, setTypeFilter] = useState(filter.type || '');
  const [loadingId, setLoadingId] = useState(null);

  // API Hooks
  const { data, isLoading, isError, refetch } = useGetAdminJobsQuery({
    page: filter.page || 1,
    limit: filter.limit || 10,
    status: statusFilter,
    category: categoryFilter,
    type: typeFilter,
    search: filter.search || '',
  });

  const { data: statsData } = useGetJobStatisticsQuery();
  const [updateStatus] = useUpdateJobStatusMutation();
  const [deleteJob] = useDeleteJobMutation();

  const jobs = data?.data || [];
  const pagination = data?.pagination || {};
  const totalPages = pagination?.totalPages || 0;
  const totalItems = pagination?.total || 0;
  const currentPage = pagination?.page || 1;
  const showPagination = totalPages > 1;
  const stats = statsData?.data || {};

  // Get status badge
  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { class: 'status-pending', label: 'Pending' },
      'approved': { class: 'status-approved', label: 'Approved' },
      'rejected': { class: 'status-rejected', label: 'Rejected' },
      'expired': { class: 'status-expired', label: 'Expired' },
    };
    const statusInfo = statusMap[status] || statusMap['pending'];
    return <span className={`employment-status-badge ${statusInfo.class}`}>
      {statusInfo.label}
    </span>;
  };

  // Get category label
  const getCategoryLabel = (category) => {
    const categoryMap = {
      'job': 'Job',
      'business': 'Business',
      'internship': 'Internship',
      'freelance': 'Freelance',
      'consultancy': 'Consultancy',
    };
    return categoryMap[category] || category;
  };

  // Get type label
  const getTypeLabel = (type) => {
    const typeMap = {
      'full_time': 'Full Time',
      'part_time': 'Part Time',
      'contract': 'Contract',
      'remote': 'Remote',
      'hybrid': 'Hybrid',
    };
    return typeMap[type] || type;
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Handle page change
  const handlePageChange = (page) => {
    dispatch(
      setEmploymentFilters({
        page,
      })
    );
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle search
  const handleSearch = (value) => {
    dispatch(
      setEmploymentFilters({
        search: value,
        page: 1,
      })
    );
  };

  // Handle limit change
  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    dispatch(
      setEmploymentFilters({
        limit: newLimit,
        page: 1,
      })
    );
  };

  // Handle status filter change
  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    dispatch(
      setEmploymentFilters({
        page: 1,
      })
    );
  };

  // Handle category filter change
  const handleCategoryFilter = (category) => {
    setCategoryFilter(category);
    dispatch(
      setEmploymentFilters({
        page: 1,
      })
    );
  };

  // Handle type filter change
  const handleTypeFilter = (type) => {
    setTypeFilter(type);
    dispatch(
      setEmploymentFilters({
        page: 1,
      })
    );
  };

  // Clear filters
  const clearFilters = () => {
    setStatusFilter('');
    setCategoryFilter('');
    setTypeFilter('');
    dispatch(
      setEmploymentFilters({
        search: '',
        page: 1,
      })
    );
  };

  // Handle status change
  const handleStatusChange = async (id, status) => {
    if (!window.confirm(`Are you sure you want to change status to "${status}"?`)) {
      return;
    }

    try {
      setLoadingId(id);
      await updateStatus({ id, status }).unwrap();
      successToast(`Status updated to ${status}`);
      refetch();
    } catch (error) {
      errorToast(error?.data?.message || 'Failed to update status');
    } finally {
      setLoadingId(null);
    }
  };

  // Handle delete
  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }

    try {
      setLoadingId(id);
      await deleteJob(id).unwrap();
      successToast('Job deleted successfully');
      refetch();
    } catch (error) {
      errorToast(error?.data?.message || 'Failed to delete job');
    } finally {
      setLoadingId(null);
    }
  };

  // Refresh
  const handleRefresh = () => {
    refetch();
    successToast('Data refreshed successfully!');
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <h5 className="mt-3">Loading Jobs...</h5>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-5">
        <div className="alert alert-danger">
          <h5>Something Went Wrong</h5>
          <p>Unable to load jobs. Please try again.</p>
          <button className="btn btn-primary" onClick={handleRefresh}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* HEADER */}
      {/* <div className="page-header-breadcrumb all_breadcrumb">
        <div className="ms-md-1 mb-1 mb-md-0 ms-0 page_heading_breadcrumb">
          <nav>
            <h4 className="fw-semibold">
              Employment & Business {totalItems > 0 && <span className="badge-custom bg-primary">{totalItems}</span>}
            </h4>
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item active">
                <Link to="/">Home</Link>
              </li>
              <li className="breadcrumb-item">Employment & Business</li>
            </ol>
          </nav>
        </div>
        <div className="page-title fw-semibold fs-18 mb-0">
          <button
            className="btn btn-primary me-2"
            onClick={handleRefresh}
          >
            🔄 Refresh
          </button>
          <button
            className="btn btn-success"
            onClick={() => navigate("/create-employment")}
          >
            <FaPlus className="me-1" /> Add Job
          </button>
        </div>
      </div> */}
<CommonBreadcrumb
    title="Employment & Business"
    titleIcon={<FaBriefcase className="me-2 text-primary" />}
    count={totalItems}
    breadcrumbs={[
        { label: "Home", link: "/" },
        { label: "Employment & Business" },
    ]}
    buttons={[
        {
            text: "Refresh",
            icon: <FaSyncAlt className="me-1" />,
            onClick: handleRefresh,
            className: "btn btn-primary",
        },
        {
            text: "Add Job",
            icon: <FaPlus className="me-1" />,
            onClick: () => navigate("/create-employment"),
            className: "btn btn-success",
        },
    ]}
/>
      {/* STATS */}
      <div className="row g-3 mb-4">
        <div className="col-md-2 col-6">
          <div className="card card_new_all text-white">
            <div className="card-body text-center">
              <h3 className="mb-0">{stats.total || 0}</h3>
              <small>Total</small>
            </div>
          </div>
        </div>
        <div className="col-md-2 col-6">
          <div className="card card_new_all text-white">
            <div className="card-body text-center">
              <h3 className="mb-0">{stats.pending || 0}</h3>
              <small>Pending</small>
            </div>
          </div>
        </div>
        <div className="col-md-2 col-6">
          <div className="card card_new_all text-white">
            <div className="card-body text-center">
              <h3 className="mb-0">{stats.approved || 0}</h3>
              <small>Approved</small>
            </div>
          </div>
        </div>
        <div className="col-md-2 col-6">
          <div className="card card_new_all text-white">
            <div className="card-body text-center">
              <h3 className="mb-0">{stats.rejected || 0}</h3>
              <small>Rejected</small>
            </div>
          </div>
        </div>
        <div className="col-md-2 col-6">
          <div className="card card_new_all text-white">
            <div className="card-body text-center">
              <h3 className="mb-0">{stats.expired || 0}</h3>
              <small>Expired</small>
            </div>
          </div>
        </div>
        <div className="col-md-2 col-6">
          <div className="card card_new_all text-white">
            <div className="card-body text-center">
              <h3 className="mb-0">{stats.featured || 0}</h3>
              <small>Featured</small>
            </div>
          </div>
        </div>
      </div>

      {/* SEARCH AND FILTER SECTION */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-center">
            <div className="col-md-3">
              <SearchBar
                search={filter.search}
                setSearch={handleSearch}
                placeholder="Search by title, company..."
              />
            </div>

            <div className="col-md-2">
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => handleStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="expired">Expired</option>
              </select>
            </div>

            <div className="col-md-2">
              <select
                className="form-select"
                value={categoryFilter}
                onChange={(e) => handleCategoryFilter(e.target.value)}
              >
                <option value="">All Categories</option>
                <option value="job">Job</option>
                <option value="business">Business</option>
                <option value="internship">Internship</option>
                <option value="freelance">Freelance</option>
                <option value="consultancy">Consultancy</option>
              </select>
            </div>

            <div className="col-md-2">
              <select
                className="form-select"
                value={typeFilter}
                onChange={(e) => handleTypeFilter(e.target.value)}
              >
                <option value="">All Types</option>
                <option value="full_time">Full Time</option>
                <option value="part_time">Part Time</option>
                <option value="contract">Contract</option>
                <option value="remote">Remote</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>

            <div className="col-md-1">
              <select
                className="form-select"
                value={filter.limit || 10}
                onChange={handleLimitChange}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>

            <div className="col-md-2 text-md-end">
              {(filter.search || statusFilter || categoryFilter || typeFilter) && (
                <button
                  className="btn btn-outline-secondary me-2"
                  onClick={clearFilters}
                >
                  <FaTimes className="me-1" />
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Active Filters */}
          {(filter.search || statusFilter || categoryFilter || typeFilter) && (
            <div className="mt-3 d-flex flex-wrap gap-2">
              <span className="text-muted me-2">Active Filters:</span>
              {filter.search && (
                <span className="badge bg-primary d-flex align-items-center gap-1">
                  Search: {filter.search}
                  <button
                    className="btn btn-link btn-sm text-white p-0 ms-1"
                    onClick={() => handleSearch('')}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {statusFilter && (
                <span className="badge bg-info d-flex align-items-center gap-1">
                  Status: {statusFilter}
                  <button
                    className="btn btn-link btn-sm text-white p-0 ms-1"
                    onClick={() => handleStatusFilter('')}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {categoryFilter && (
                <span className="badge bg-success d-flex align-items-center gap-1">
                  Category: {categoryFilter}
                  <button
                    className="btn btn-link btn-sm text-white p-0 ms-1"
                    onClick={() => handleCategoryFilter('')}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {typeFilter && (
                <span className="badge bg-warning text-dark d-flex align-items-center gap-1">
                  Type: {typeFilter}
                  <button
                    className="btn btn-link btn-sm text-dark p-0 ms-1"
                    onClick={() => handleTypeFilter('')}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* TABLE */}
      <div className="st-wrapper-card">
        {jobs.length === 0 ? (
          <div className="text-center py-5">
            <div className="mb-3">
              <FaBriefcase className="text-muted" size={48} />
            </div>
            <h5>No Jobs Found</h5>
            <p className="text-muted">
              {filter.search || statusFilter || categoryFilter || typeFilter
                ? "Try adjusting your search or filter criteria"
                : "No jobs available"}
            </p>
            {(filter.search || statusFilter || categoryFilter || typeFilter) && (
              <button className="btn btn-primary" onClick={clearFilters}>
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <div className="st-table-responsive">
            <table className="table st-custom-table align-middle mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Job Title</th>
                  <th>Company</th>
                  <th>Category</th>
                  <th>Type</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Applications</th>
                  <th>Created</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((item, index) => (
                  <tr key={item.id}>
                    <td>{(currentPage - 1) * (filter.limit || 10) + index + 1}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        {item.company_logo && (
                          <img
                            src={item.company_logo}
                            alt={item.company_name}
                            style={{ width: '30px', height: '30px', objectFit: 'cover' }}
                            className="rounded"
                            onError={(e) => {
                              e.target.src = '/placeholder-logo.png';
                            }}
                          />
                        )}
                        <div>
                          <div className="fw-semibold">{item.title}</div>
                          {item.is_featured && (
                            <span className="badge bg-warning text-dark">
                              <FaStar className="me-1" /> Featured
                            </span>
                          )}
                          {item.is_verified && (
                            <span className="badge bg-success">
                              <FaCheckCircle className="me-1" /> Verified
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>{item.company_name}</td>
                    <td>
                      <span className="badge bg-secondary">{getCategoryLabel(item.category)}</span>
                    </td>
                    <td>
                      <span className="badge bg-info text-dark">{getTypeLabel(item.type)}</span>
                    </td>
                    <td>
                      {item.location && (
                        <div className="d-flex align-items-center gap-1">
                          <FaMapMarkerAlt size={12} />
                          <span className="text-muted small">{item.location}</span>
                        </div>
                      )}
                    </td>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        value={item.status}
                        onChange={(e) => handleStatusChange(item.id, e.target.value)}
                        disabled={loadingId === item.id}
                        style={{ minWidth: '110px' }}
                      >
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="expired">Expired</option>
                      </select>
                    </td>
                    <td>
                      <span className="badge bg-primary">{item.application_count || 0}</span>
                    </td>
                    <td>{formatDate(item.created_at)}</td>
                    <td className="text-center">
                      {/* VIEW */}
                      <button
                        className="st-action-btn st-view-btn me-1"
                        onClick={() =>
                          dispatch(
                            openModal({
                              type: "view-employment",
                              title: "View Job Details",
                              data: item,
                            })
                          )
                        }
                        title="View Details"
                        disabled={loadingId === item.id}
                      >
                        <FaEye />
                      </button>

                      {/* EDIT */}
                      <button
                        className="st-action-btn st-edit-btn me-1"
                        onClick={() => navigate(`/edit-employment/${item.id}`)}
                        title="Edit"
                        disabled={loadingId === item.id}
                      >
                        <FaEdit />
                      </button>

                      {/* APPLICATIONS */}
                      <button
                        className="st-action-btn st-status-btn me-1"
                        onClick={() =>
                          dispatch(
                            openModal({
                              type: "view-applications",
                              title: "Job Applications",
                              data: { job_id: item.id, title: item.title },
                            })
                          )
                        }
                        title="View Applications"
                        disabled={loadingId === item.id}
                      >
                        <FaUser />
                      </button>

                      {/* DELETE */}
                      <button
                        className="st-action-btn st-delete-btn"
                        onClick={() => handleDelete(item.id, item.title)}
                        title="Delete"
                        disabled={loadingId === item.id}
                      >
                        {loadingId === item.id ? (
                          <span className="spinner-border spinner-border-sm" />
                        ) : (
                          <FaTrash />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* PAGINATION */}
        {showPagination && jobs.length > 0 && (
          <div className="d-flex flex-wrap align-items-center justify-content-between mt-4">
            <div className="text-muted small mb-2 mb-md-0">
              Showing {((currentPage - 1) * (filter.limit || 10)) + 1} to{' '}
              {Math.min(currentPage * (filter.limit || 10), totalItems)} of {totalItems} entries
            </div>
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default Employment;